import React from 'react';
import SafeImage from '../../Utils/ImageHelper';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Helmet } from 'react-helmet-async';

function OverviewTab({
  paymentMethods,
  bankBalances,
  setActiveTab,
  setActiveLedgerTab,
  companyLanguage,
  kpiData,
  chartData,
  totalReceivable,
  totalPayable,
  company
}) {
  return (
    <div className="space-y-6">
      <Helmet>
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Helmet>
      {/* Bank Cards + Receivable/Payable Cards */}
      <div className="mb-6">
        <h2 className={`text-2xl font-bold text-brand-header mb-6 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          {companyLanguage === 'ur' ? 'بینکس اور کھاتے' : 'Banks & Accounts'}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {paymentMethods && paymentMethods.filter(pm => pm.isActive).map((bank, index) => {
              const balance = bankBalances[bank._id] !== undefined 
                ? bankBalances[bank._id] 
                : (bank.openingBalance || 0);
              
              // Different gradient colors for variety
              const gradients = [
                { from: 'from-purple-500', to: 'to-purple-700', shadow: 'shadow-purple-200' },
                { from: 'from-blue-500', to: 'to-blue-700', shadow: 'shadow-blue-200' },
                { from: 'from-emerald-500', to: 'to-emerald-700', shadow: 'shadow-emerald-200' },
                { from: 'from-orange-500', to: 'to-orange-700', shadow: 'shadow-orange-200' },
                { from: 'from-pink-500', to: 'to-pink-700', shadow: 'shadow-pink-200' },
                { from: 'from-teal-500', to: 'to-teal-700', shadow: 'shadow-teal-200' }
              ];
              const gradient = gradients[index % gradients.length];
              
              return (
                <div 
                  key={bank._id}
                  className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] cursor-pointer group"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                  onClick={() => {
                    setActiveTab("ledgers");
                    setActiveLedgerTab("bankKhata");
                  }}
                >
                  {/* Animated Shine Effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                  </div>

                  {/* Gradient Header */}
                  <div className={`bg-gradient-to-br ${gradient.from} ${gradient.to} h-32 relative overflow-hidden`}>
                    {/* Animated Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                        backgroundSize: '20px 20px'
                      }}></div>
                    </div>
                    
                    {/* Floating Orbs */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                    
                    {/* Icon in Top Right */}
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    
                    {/* Centered Content */}
                    <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
                      <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {companyLanguage === 'ur' ? 'موجودہ بیلنس' : 'Current Balance'}
                      </p>
                      <p className={`text-4xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {balance.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Card Body */}
                  <div className="p-5 bg-white relative">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-base font-bold text-gray-800 group-hover:text-gray-900 transition-colors ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {companyLanguage === 'ur' ? (bank.nameUrdu || bank.name) : bank.name}
                      </h3>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 ${
                        balance >= 0 
                          ? 'bg-emerald-500 text-white shadow-sm' 
                          : 'bg-red-500 text-white shadow-sm'
                      }`}>
                        {balance >= 0 ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 group-hover:border-gray-200 transition-colors">
                      <span className={`text-xs font-medium text-gray-500 group-hover:text-gray-700 transition-colors ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {companyLanguage === 'ur' ? 'تفصیلات دیکھیں' : 'View Details'}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-brand-header transform group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Hover Effect Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradient.from} ${gradient.to} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none rounded-2xl`}></div>
                  
                  {/* Glow Effect on Hover */}
                  <div className={`absolute -inset-1 bg-gradient-to-r ${gradient.from} ${gradient.to} rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10`}></div>
                </div>
              );
            })}
          
          {/* Receivable Card */}
          <div 
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer relative"
            style={{
              animation: `fadeInUp 0.6s ease-out ${(paymentMethods.filter(pm => pm.isActive).length) * 0.1}s both`
            }}
            onClick={() => {
              setActiveTab("ledgers");
              setActiveLedgerTab("receivableKhata");
            }}
          >
            {/* Animated Shine Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            </div>

            {/* Green Gradient Header */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 h-32 relative overflow-hidden">
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: '20px 20px'
                }}></div>
              </div>
              
              {/* Floating Orbs */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              {/* Icon in Top Right */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              {/* Centered Content */}
              <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
                <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'موجودہ بیلنس' : 'Current Balance'}
                </p>
                <p className={`text-4xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {(totalReceivable || 0).toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Card Body */}
            <div className="p-5 bg-white relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-base font-bold text-gray-800 group-hover:text-gray-900 transition-colors ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'قابل وصول' : 'Receivable'}
                </h3>
                <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 group-hover:border-gray-200 transition-colors">
                <span className={`text-xs font-medium text-gray-500 group-hover:text-gray-700 transition-colors ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'تفصیلات دیکھیں' : 'View Details'}
                </span>
                <svg className="w-4 h-4 text-gray-400 group-hover:text-brand-header transform group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-700 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>
            
            {/* Glow Effect on Hover */}
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
          </div>

          {/* Payable Card */}
          <div 
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer relative"
            style={{
              animation: `fadeInUp 0.6s ease-out ${(((paymentMethods && paymentMethods.filter(pm => pm.isActive).length) || 0) + 1) * 0.1}s both`
            }}
            onClick={() => {
              setActiveTab("ledgers");
              setActiveLedgerTab("payableKhata");
            }}
          >
            {/* Animated Shine Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            </div>

            {/* Red Gradient Header */}
            <div className="bg-gradient-to-br from-red-500 to-red-700 h-32 relative overflow-hidden">
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: '20px 20px'
                }}></div>
              </div>
              
              {/* Floating Orbs */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              {/* Icon in Top Right */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              
              {/* Centered Content */}
              <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
                <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'موجودہ بیلنس' : 'Current Balance'}
                </p>
                <p className={`text-4xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {(totalPayable || 0).toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Card Body */}
            <div className="p-5 bg-white relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-base font-bold text-gray-800 group-hover:text-gray-900 transition-colors ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'قابل ادائیگی' : 'Payable'}
                </h3>
                <span className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 group-hover:border-gray-200 transition-colors">
                <span className={`text-xs font-medium text-gray-500 group-hover:text-gray-700 transition-colors ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'تفصیلات دیکھیں' : 'View Details'}
                </span>
                <svg className="w-4 h-4 text-gray-400 group-hover:text-brand-header transform group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-700 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>
            
            {/* Glow Effect on Hover */}
            <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-red-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Bills Card */}
        <div 
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer relative"
          style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}
        >
          {/* Animated Shine Effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
          </div>

          {/* Blue Gradient Header */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-700 h-28 relative overflow-hidden">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: '20px 20px'
              }}></div>
            </div>
            
            {/* Floating Orbs */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            {/* Icon in Top Right */}
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            
            {/* Centered Content */}
            <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
              <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'کل بلز' : 'Total Bills'}
              </p>
              <p className={`text-4xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.totalBills}
              </p>
            </div>
          </div>
          
          {/* Card Body */}
          <div className="p-5 bg-white relative">
            <div className="flex items-center justify-between mb-3">
              <p className={`text-xs text-gray-500 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.billsChange >= 0 ? '+' : ''}{kpiData.billsChange} {companyLanguage === 'ur' ? 'گزشتہ مہینے سے' : 'from last month'}
              </p>
              <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </div>
          
          {/* Glow Effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
        </div>

        {/* Total Sales Card */}
        <div 
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer relative"
          style={{ animation: 'fadeInUp 0.6s ease-out 0.4s both' }}
        >
          {/* Animated Shine Effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
          </div>

          {/* Green Gradient Header */}
          <div className="bg-gradient-to-br from-green-500 to-green-700 h-28 relative overflow-hidden">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: '20px 20px'
              }}></div>
            </div>
            
            {/* Floating Orbs */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            {/* Icon in Top Right */}
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            
            {/* Centered Content */}
            <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
              <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'کل فروخت' : 'Total Sales'}
              </p>
              <p className={`text-4xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.totalSales}
              </p>
            </div>
          </div>
          
          {/* Card Body */}
          <div className="p-5 bg-white relative">
            <div className="flex items-center justify-between mb-3">
              <p className={`text-xs text-gray-500 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.salesChange >= 0 ? '+' : ''}{kpiData.salesChange} {companyLanguage === 'ur' ? 'گزشتہ مہینے سے' : 'from last month'}
              </p>
              <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </div>
          
          {/* Glow Effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-green-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
        </div>

        {/* Total Recoveries Card */}
        <div 
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer relative"
          style={{ animation: 'fadeInUp 0.6s ease-out 0.5s both' }}
        >
          {/* Animated Shine Effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
          </div>

          {/* Purple Gradient Header */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-700 h-28 relative overflow-hidden">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: '20px 20px'
              }}></div>
            </div>
            
            {/* Floating Orbs */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            {/* Icon in Top Right */}
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            
            {/* Centered Content */}
            <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
              <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'کل وصولی' : 'Total Recoveries'}
              </p>
              <p className={`text-4xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.totalRecoveries}
              </p>
            </div>
          </div>
          
          {/* Card Body */}
          <div className="p-5 bg-white relative">
            <div className="flex items-center justify-between mb-3">
              <p className={`text-xs text-gray-500 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.recoveriesChange >= 0 ? '+' : ''}{kpiData.recoveriesChange} {companyLanguage === 'ur' ? 'گزشتہ مہینے سے' : 'from last month'}
              </p>
              <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </div>
          
          {/* Glow Effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
        </div>

        {/* Total Expenses Card */}
        <div 
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer relative"
          style={{ animation: 'fadeInUp 0.6s ease-out 0.6s both' }}
        >
          {/* Animated Shine Effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
          </div>

          {/* Red Gradient Header */}
          <div className="bg-gradient-to-br from-red-500 to-red-700 h-28 relative overflow-hidden">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: '20px 20px'
              }}></div>
            </div>
            
            {/* Floating Orbs */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            {/* Icon in Top Right */}
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            
            {/* Centered Content */}
            <div className="relative h-full flex flex-col items-center justify-center px-6 pt-8 pb-4">
              <p className={`text-white/90 text-sm font-medium mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'کل اخراجات' : 'Total Expenses'}
              </p>
              <p className={`text-3xl font-bold text-white transform group-hover:scale-105 transition-transform duration-300 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.totalExpenses.toLocaleString()}
              </p>
            </div>
          </div>
          
          {/* Card Body */}
          <div className="p-5 bg-white relative">
            <div className="flex items-center justify-between mb-3">
              <p className={`text-xs text-gray-500 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {kpiData.expensesChange >= 0 ? '+' : ''}{kpiData.expensesChange.toLocaleString()} {companyLanguage === 'ur' ? 'گزشتہ مہینے سے' : 'from last month'}
              </p>
              <span className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </div>
          
          {/* Glow Effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-red-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
        </div>
      </div>

      {/* Charts Section */}
      {chartData && chartData.length > 0 ? (
        <div className="space-y-6 animate-fade-in-up">
          {/* Monthly Performance Chart */}
          <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card">
            <h2 className={`text-xl font-bold text-brand-header mb-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'ماہانہ کارکردگی' : 'Monthly Performance'}
            </h2>
            <ResponsiveContainer width="100%" height={300} minHeight={300}>
              <BarChart 
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#374151', fontSize: 12 }}
                  style={{ fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif" : 'inherit' }}
                />
                <YAxis 
                  tick={{ fill: '#374151', fontSize: 12 }}
                  domain={[0, 'auto']}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '10px'
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                <Bar dataKey="bills" fill="#3b82f6" name={companyLanguage === 'ur' ? 'بلز' : 'Bills'} radius={[4, 4, 0, 0]} />
                <Bar dataKey="sales" fill="#10b981" name={companyLanguage === 'ur' ? 'فروخت' : 'Sales'} radius={[4, 4, 0, 0]} />
                <Bar dataKey="recoveries" fill="#8b5cf6" name={companyLanguage === 'ur' ? 'وصولی' : 'Recoveries'} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Income vs Expenses Chart */}
          <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card">
            <h2 className={`text-xl font-bold text-brand-header mb-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'آمدنی بمقابلہ اخراجات' : 'Income vs Expenses'}
            </h2>
            <ResponsiveContainer width="100%" height={300} minHeight={300}>
              <LineChart 
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#374151', fontSize: 12 }}
                  style={{ fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif" : 'inherit' }}
                />
                <YAxis 
                  tick={{ fill: '#374151', fontSize: 12 }}
                  domain={[0, 'auto']}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '10px'
                  }}
                  formatter={(value) => `Rs. ${value.toLocaleString()}`}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="income" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  name={companyLanguage === 'ur' ? 'آمدنی' : 'Income'}
                  dot={{ fill: '#10b981', r: 6, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 8 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="expenses" 
                  stroke="#ef4444" 
                  strokeWidth={3}
                  name={companyLanguage === 'ur' ? 'خرچ' : 'Expenses'}
                  dot={{ fill: '#ef4444', r: 6, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 8 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}


      {/* Company Info Card */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card animate-fade-in-up">
        <h2 className={`text-xl font-bold text-brand-header mb-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>{companyLanguage === 'ur' ? 'معلومات' : 'Company Information'}</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{companyLanguage === 'ur' ? 'نام' : 'Username'}</label>
            <p className="text-gray-900 font-semibold">{company?.username}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{companyLanguage === 'ur' ? 'ای میل' : 'Email'}</label>
            <p className="text-gray-900 font-semibold">{company?.email}</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{companyLanguage === 'ur' ? 'پتہ' : 'Address'}</label>
            <p className="text-gray-900">{company?.address || (companyLanguage === 'ur' ? 'سیٹ نہیں' : "Not set")}</p>
          </div>
          {company?.logo && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{companyLanguage === 'ur' ? 'لوگو' : 'Logo'}</label>
              <SafeImage
                src={company.logo}
                alt="Company Logo"
                className="h-32 w-32 object-contain border-2 border-brand-card rounded-lg shadow-md"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (!e.target.nextElementSibling || !e.target.nextElementSibling.classList.contains('image-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'image-error h-32 w-32 flex items-center justify-center border-2 border-brand-card rounded-lg shadow-md bg-gray-100';
                    errorDiv.innerHTML = `<p class="text-xs text-gray-500">${companyLanguage === 'ur' ? 'تصویر دستیاب نہیں' : 'Image not available'}</p>`;
                    e.target.parentNode.appendChild(errorDiv);
                  }
                }}
              />
            </div>
          )}
        </div>
        <button
          onClick={() => setActiveTab("profile")}
          className="mt-4 bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg"
        >
          {companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit Profile'}
        </button>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card animate-fade-in-up">
        <h2 className={`text-xl font-bold text-brand-header mb-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>{companyLanguage === 'ur' ? 'جلدی کام' : 'Quick Actions'}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setActiveTab("profile")}
            className="flex items-center gap-3 p-4 bg-brand-background hover:bg-brand-card rounded-lg transition-all text-left transform hover:scale-105 hover:shadow-md border border-brand-card"
          >
            <div className="bg-brand-header rounded-full p-2">
              <svg className="w-6 h-6 text-brand-button" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-brand-header">{companyLanguage === 'ur' ? 'پروفائل بدلیں' : 'Update Profile'}</p>
              <p className="text-sm text-gray-600">{companyLanguage === 'ur' ? 'دکان، لوگو، اور پتہ بدلیں' : 'Edit shop name, logo, and address'}</p>
            </div>
          </button>

          <button
            onClick={() => setActiveTab("persons")}
            className="flex items-center gap-3 p-4 bg-brand-background hover:bg-brand-card rounded-lg transition-all text-left transform hover:scale-105 hover:shadow-md border border-brand-card"
          >
            <div className="bg-brand-header rounded-full p-2">
              <svg className="w-6 h-6 text-brand-button" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-brand-header">{companyLanguage === 'ur' ? 'رابطہ شامل کریں' : 'Manage Persons'}</p>
              <p className="text-sm text-gray-600">{companyLanguage === 'ur' ? 'رابطہ شامل یا بدلیں' : 'Add or edit contact persons'}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default OverviewTab;


